/**
 * Snippets-Verwaltung (Text-Bausteine)
 *
 * CRUD für kleine wiederverwendbare Textbausteine, die im Schreiben-Editor
 * via „Bausteine einfügen"-Button an der Cursor-Position eingesetzt werden.
 *
 * Layout:
 *   ┌──────────────── Kategorien (links) ─┬───────── Editor / Preview ─┐
 *   │  Alle · Anrede · Schluss · …        │ Name · Kategorie · Inhalt   │
 *   │  + Filter, Neu-Baustein-Button      │ Live-Vorschau (HTML)        │
 *   └─────────────────────────────────────┴─────────────────────────────┘
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Plus, PencilSimple, Trash, Stack, MagnifyingGlass, FloppyDisk, X, Copy,
} from "@phosphor-icons/react";

const DEFAULT_CATEGORIES = [
  "Anrede", "Schluss", "Zahlungsverzug", "Nebenkosten",
  "Kündigung", "Fristen", "Datenschutz", "Allgemein",
];

export default function SnippetsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {id?, name, text, category}
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/snippets");
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const set = new Set(DEFAULT_CATEGORIES);
    for (const s of items) if (s.category) set.add(s.category);
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (filterCategory && s.category !== filterCategory) return false;
      if (q && !(s.name + " " + (s.text || "") + " " + (s.category || "")).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filterCategory, search]);

  const selected = items.find((s) => s.id === selectedId);

  const startNew = () => {
    setEditing({ name: "", text: "", category: filterCategory || "Allgemein" });
    setSelectedId(null);
  };
  const startEdit = (s) => {
    setEditing({ id: s.id, name: s.name, text: s.text || "", category: s.category || "Allgemein" });
  };
  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Name fehlt"); return; }
    if (!editing.text?.trim()) { toast.error("Inhalt fehlt"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        const { data } = await api.patch(`/snippets/${editing.id}`, {
          name: editing.name.trim(), text: editing.text, category: editing.category || "Allgemein",
        });
        setItems((prev) => prev.map((s) => (s.id === data.id ? data : s)));
        toast.success("Baustein gespeichert");
      } else {
        const { data } = await api.post("/snippets", {
          name: editing.name.trim(), text: editing.text, category: editing.category || "Allgemein",
        });
        setItems((prev) => [...prev, data]);
        setSelectedId(data.id);
        toast.success("Baustein angelegt");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    const ok = await confirm({
      title: "Baustein löschen?",
      description: `„${s.name}" wird endgültig gelöscht. Dieser Schritt kann nicht rückgängig gemacht werden.`,
      confirmLabel: "Löschen",
      confirmClassName: "bg-rose-600 hover:bg-rose-700",
    });
    if (!ok) return;
    try {
      await api.delete(`/snippets/${s.id}`);
      setItems((prev) => prev.filter((x) => x.id !== s.id));
      if (selectedId === s.id) setSelectedId(null);
      toast.success("Baustein gelöscht");
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const copyPlaceholder = (s) => {
    const html = s.text || "";
    if (navigator.clipboard && html) {
      navigator.clipboard.writeText(html.replace(/<[^>]+>/g, ""));
      toast.success("In Zwischenablage kopiert (nur Text)");
    }
  };

  return (
    <div className="space-y-4" data-testid="snippets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Stack size={22} /> Bausteine
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Wiederverwendbare Textbausteine für den Schreiben-Editor. Fügen sich an der Cursor-Position ein.
          </p>
        </div>
        <Button onClick={startNew} className="gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="snippet-new-btn">
          <Plus size={14} /> Neuer Baustein
        </Button>
      </div>

      <div className="grid grid-cols-[220px_1fr_1fr] gap-3 min-h-[600px]">
        {/* Kategorien-Sidebar */}
        <div className="bg-white border border-slate-200 rounded-md p-2 space-y-1">
          <button
            onClick={() => setFilterCategory("")}
            className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${filterCategory === "" ? "bg-slate-900 text-white font-semibold" : "hover:bg-slate-100"}`}
            data-testid="snippet-cat-all"
          >
            Alle ({items.length})
          </button>
          {categories.map((c) => {
            const cnt = items.filter((s) => s.category === c).length;
            return (
              <button
                key={c}
                onClick={() => setFilterCategory(c)}
                className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${filterCategory === c ? "bg-slate-900 text-white font-semibold" : "hover:bg-slate-100"} ${cnt === 0 ? "text-slate-400" : ""}`}
                data-testid={`snippet-cat-${c}`}
              >
                {c} <span className="text-[10px] tabular-nums opacity-70">({cnt})</span>
              </button>
            );
          })}
        </div>

        {/* Liste */}
        <div className="bg-white border border-slate-200 rounded-md flex flex-col min-h-0">
          <div className="p-2 border-b border-slate-100 relative">
            <MagnifyingGlass size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Bausteine suchen…"
              className="h-8 pl-7 text-sm"
              data-testid="snippet-search"
            />
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="snippet-list">
            {loading && <div className="p-4 text-sm text-slate-500 text-center">Lade …</div>}
            {!loading && filtered.length === 0 && (
              <div className="p-8 text-sm text-slate-500 text-center">
                {items.length === 0
                  ? "Noch keine Bausteine. Lege deinen ersten Baustein an."
                  : "Keine Treffer für diese Filter."}
              </div>
            )}
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => { setSelectedId(s.id); setEditing(null); }}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors ${selectedId === s.id ? "bg-emerald-50 border-l-2 border-emerald-500" : ""}`}
                data-testid={`snippet-row-${s.id}`}
              >
                <div className="text-sm font-medium text-slate-800 truncate">{s.name}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 uppercase tracking-wider font-semibold">{s.category || "—"}</span>
                  <span className="truncate">{(s.text || "").replace(/<[^>]+>/g, " ").slice(0, 60)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Editor / Preview */}
        <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-col min-h-0" data-testid="snippet-editor-panel">
          {editing ? (
            <div className="space-y-3 flex-1 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">{editing.id ? "Baustein bearbeiten" : "Neuer Baustein"}</div>
                <button onClick={cancelEdit} className="text-slate-500 hover:text-slate-800" data-testid="snippet-cancel-btn">
                  <X size={16} />
                </button>
              </div>
              <div>
                <Label>Name</Label>
                <Input value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="z.B. Standardschluss"
                  data-testid="snippet-name" />
              </div>
              <div>
                <Label>Kategorie</Label>
                <div className="flex gap-2">
                  <select
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="h-9 text-sm border border-slate-200 rounded px-2 bg-white flex-1"
                    data-testid="snippet-category"
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    {!categories.includes(editing.category) && editing.category && (
                      <option value={editing.category}>{editing.category}</option>
                    )}
                  </select>
                  <Input
                    placeholder="Neue Kategorie…"
                    className="w-32"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.target.value.trim()) {
                        setEditing({ ...editing, category: e.target.value.trim() });
                        e.target.value = "";
                      }
                    }}
                    data-testid="snippet-new-category"
                  />
                </div>
              </div>
              <div className="flex-1 flex flex-col min-h-0">
                <Label>Inhalt (HTML oder Text; Makros wie <span className="font-mono">{"{{recipient.first_name}}"}</span> werden im Editor gerendert)</Label>
                <Textarea
                  value={editing.text}
                  onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                  className="font-mono text-xs flex-1 min-h-[240px]"
                  placeholder="<p>Beispiel: Mit freundlichen Grüßen<br/>Ihre Hausverwaltung</p>"
                  data-testid="snippet-text"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Live-Vorschau</div>
                <div className="border border-slate-200 rounded p-3 bg-slate-50 text-sm prose prose-sm max-w-none" data-testid="snippet-preview"
                     dangerouslySetInnerHTML={{ __html: editing.text || "<em>Leer</em>" }} />
              </div>
              <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={cancelEdit} disabled={saving} data-testid="snippet-cancel">
                  Abbrechen
                </Button>
                <Button onClick={save} disabled={saving} className="gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="snippet-save">
                  <FloppyDisk size={14} /> {saving ? "Speichere…" : "Speichern"}
                </Button>
              </div>
            </div>
          ) : selected ? (
            <div className="space-y-3 flex-1 flex flex-col">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold text-slate-800">{selected.name}</div>
                  <div className="text-[11px] text-slate-500">
                    Kategorie: <span className="font-semibold">{selected.category || "—"}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => copyPlaceholder(selected)} className="gap-1" data-testid="snippet-copy">
                    <Copy size={12} /> Kopieren
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => startEdit(selected)} className="gap-1" data-testid="snippet-edit-btn">
                    <PencilSimple size={12} /> Bearbeiten
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(selected)} className="gap-1 text-rose-700 hover:text-rose-800 hover:bg-rose-50 border-rose-200" data-testid="snippet-delete-btn">
                    <Trash size={12} /> Löschen
                  </Button>
                </div>
              </div>
              <div className="flex-1 flex flex-col min-h-0 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Vorschau</div>
                  <div className="border border-slate-200 rounded p-4 bg-white text-sm prose prose-sm max-w-none"
                       dangerouslySetInnerHTML={{ __html: selected.text || "<em>Leer</em>" }} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Quelltext</div>
                  <pre className="border border-slate-200 rounded p-3 bg-slate-50 text-[11px] font-mono overflow-auto max-h-40">
                    {selected.text}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center text-slate-500 text-sm">
              <div>
                <Stack size={40} className="mx-auto text-slate-300 mb-2" />
                <div>Wähle einen Baustein aus der Liste,</div>
                <div>oder lege einen neuen an.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
